import React from "react";
import {
  ArrowUpRight,
  Briefcase,
  TrendingUp,
  FileSignature,
  ShieldCheck,
  Scale,
} from "lucide-react";
import { Eyebrow, Reveal } from "./_shared";
import { useTheme } from "../../theme/ThemeProvider";

const AREAS = [
  {
    n: "01",
    title: "Mergers & Acquisitions",
    body:
      "Strategic counsel through deal origination, diligence, negotiation, closing and post-closing integration.",
    icon: Briefcase,
    accent: "bg-sage-600",
  },
  {
    n: "02",
    title: "Private Equity",
    body:
      "Fund formation, portfolio investments, investment manager and portfolio compliance, and exits.",
    icon: TrendingUp,
    accent: "bg-ochre-500",
  },
  {
    n: "03",
    title: "General Commercial Transactions",
    body:
      "Partnerships, joint ventures, distribution, licensing, and commercial agreements that withstand scrutiny.",
    icon: FileSignature,
    accent: "bg-rust-500",
  },
  {
    n: "04",
    title: "Regulatory Compliance",
    body:
      "Programs, policies, and risk controls that align with evolving regulatory expectations.",
    icon: ShieldCheck,
    accent: "bg-ochre-600",
  },
  {
    n: "05",
    title: "Corporate Governance Frameworks",
    body:
      "Board charters, committee structures, policies and reporting frameworks that stand up to scrutiny.",
    icon: Scale,
    accent: "bg-sage-700",
  },
];

export default function PracticeAreas() {
  const { theme } = useTheme();
  const bold = theme === "bold";

  return (
    <section
      id="practice"
      data-testid="practice-section"
      className="py-16 lg:py-20 bg-cream-100"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-10">
          <div className="lg:col-span-5">
            <Eyebrow>Services</Eyebrow>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-charcoal leading-[1.05]">
              Five disciplines.
              <span className="block italic text-sage-600">One standard.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pt-3">
            <p className="text-[15px] sm:text-base text-charcoal-muted font-sans leading-relaxed">
              Our work sits at the intersection of corporate strategy and
              commercial execution. We do fewer things, deliberately, and we do
              them with rigour.
            </p>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-cream-300">
          {AREAS.map((a, idx) => {
            const Icon = a.icon;
            return (
              <Reveal
                as="article"
                key={a.n}
                delay={idx * 0.08}
                data-testid={`practice-card-${a.n}`}
                className="group relative bg-cream-50 p-6 hover:bg-cream-100 transition-colors duration-500"
              >
                {bold && (
                  <span
                    className={`absolute left-0 top-0 bottom-0 w-1 ${a.accent}`}
                    aria-hidden="true"
                  />
                )}
                <div className="flex items-start justify-between">
                  <span className="font-serif italic text-sage-600 text-sm">— {a.n}</span>
                  <ArrowUpRight
                    size={16}
                    className="text-charcoal-muted group-hover:text-sage-700 group-hover:-translate-y-1 group-hover:translate-x-1 transition-all duration-300"
                  />
                </div>
                <Icon
                  size={22}
                  strokeWidth={1.3}
                  className={`mt-5 ${bold ? "text-charcoal" : "text-sage-600"}`}
                />
                <h3 className="mt-4 font-serif text-lg lg:text-[20px] text-charcoal leading-snug">
                  {a.title}
                </h3>
                <p className="mt-2 text-charcoal-muted font-sans text-[13px] leading-relaxed">
                  {a.body}
                </p>
                <div
                  className={`mt-5 h-px w-10 ${bold ? a.accent : "bg-sage-600"} group-hover:w-20 transition-all duration-500`}
                />
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
